import React, { useEffect, useState } from 'react';
import { Form, Input, Button, Card, Typography, message, Select } from 'antd';
import { UserOutlined, LockOutlined, MailOutlined } from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import { register } from '../../../redux/slices/authSlice';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import styled from 'styled-components';
import api from '../../../utils/api';

const { Title } = Typography;
const { Option } = Select;

const Container = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background: #f0f2f5;
`;

const StyledCard = styled(Card)`
  width: 100%;
  max-width: 400px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
`;

const Register = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { loading } = useAppSelector((state) => state.auth);
  const [areas, setAreas] = useState([]);

  useEffect(() => {
    const fetchAreas = async () => {
      try {
        const response = await api.get('/public/areas');
        setAreas(response.data.data);
      } catch (error) {
        message.error({ content: 'Failed to fetch areas' });
      }
    };
    fetchAreas();
  }, []);

  const onFinish = async (values) => {
    try {
      const resultAction = await dispatch(register(values));
      if (register.fulfilled.match(resultAction)) {
        const { user } = resultAction.payload;
        message.success({ content: 'Registration successful!' });
        switch (user.role) {
          case 'ADMIN':
            navigate('/admin/dashboard');
            break;
          case 'REPRESENTATIVE':
            navigate('/representative/dashboard');
            break;
          case 'CITIZEN':
            navigate('/citizen/dashboard');
            break;
          case 'VOLUNTEER':
            navigate('/volunteer/dashboard');
            break;
        }
      } else if (register.rejected.match(resultAction)) {
        message.error({ 
          content: resultAction.payload?.toString() || 'Registration failed. Please try again.'
        });
      }
    } catch (err) {
      message.error({ content: 'Registration failed. Please try again.' });
    }
  };

  return (
    <Container>
      <StyledCard>
        <Title level={2} style={{ textAlign: 'center', marginBottom: 32 }}>
          Register for Uttardayitav
        </Title>
        <Form
          name="register"
          onFinish={onFinish}
          layout="vertical"
          requiredMark={false}
        >
          <Form.Item
            name="name"
            rules={[{ required: true, message: 'Please input your name!' }]}
          >
            <Input
              prefix={<UserOutlined />}
              placeholder="Full Name"
              size="large"
            />
          </Form.Item>

          <Form.Item
            name="email"
            rules={[
              { required: true, message: 'Please input your email!' },
              { type: 'email', message: 'Please enter a valid email!' },
            ]}
          >
            <Input
              prefix={<MailOutlined />}
              placeholder="Email"
              size="large"
            />
          </Form.Item>

          <Form.Item
            name="password"
            rules={[
              { required: true, message: 'Please input your password!' },
              { min: 6, message: 'Password must be at least 6 characters!' },
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="Password"
              size="large"
            />
          </Form.Item>

          <Form.Item
            name="role"
            rules={[{ required: true, message: 'Please select your role!' }]}
          >
            <Select placeholder="Select Role" size="large">
              <Option value="CITIZEN">Citizen</Option>
              <Option value="VOLUNTEER">Volunteer</Option>
              <Option value="REPRESENTATIVE">Representative</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="areaId"
            rules={[{ required: true, message: 'Please select your area!' }]}
          >
            <Select
              placeholder="Select Area"
              size="large"
              showSearch
              optionFilterProp="children"
            >
              {areas.map((area) => (
                <Option key={area.id} value={area.id}>
                  {area.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              block
              loading={loading}
            >
              Register
            </Button>
          </Form.Item>

          <div style={{ textAlign: 'center' }}>
            Already have an account?{' '}
            <Link to="/login">Login now!</Link>
          </div>
        </Form>
      </StyledCard>
    </Container>
  );
};

export default Register;
